import React, { useEffect } from "react";
import {
	Button,
	PasswordInput,
	Stack,
	TextInput,
	Title,
	Paper,
	Container,
	Center,
	Loader,
	Alert,
	Box,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { CircleAlert } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAuth } from "@admin/hooks/use-auth";
import { paths } from "@admin/routes/paths";
import { loginSchema, LoginInput } from "@backend/schemas/auth.schema";

// Debug function to help track render cycles
function useRenderCounter(componentName: string) {
	const renderCount = React.useRef(0);
	console.log(`${componentName} render #${++renderCount.current}`);
}

export default function LoginPage() {
	// Debug render count
	useRenderCounter("LoginPage");

	const { login, isLoggingIn, isAuthenticated, isInitialized } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	// Extract error message from URL if present
	const searchParams = new URLSearchParams(location.search);
	const errorMessage = searchParams.get("error");

	// Clear error from URL if present (without causing a navigation)
	useEffect(() => {
		if (errorMessage && window.history) {
			// Use history API to replace the URL without navigating
			const newUrl = window.location.pathname;
			window.history.replaceState({}, "", newUrl);
			console.log("Cleared error from URL without navigation");
		}
	}, [errorMessage]);

	// Remember email across page loads
	const savedEmail = sessionStorage.getItem("loginEmail") || "";

	// Initialize form with React Hook Form
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<LoginInput>({
		defaultValues: {
			email: savedEmail,
			password: "",
		},
		resolver: zodResolver(loginSchema),
	});

	// Watch email and save to sessionStorage when it changes
	const emailValue = watch("email");

	useEffect(() => {
		if (emailValue) {
			console.log("Saving email to sessionStorage:", emailValue);
			sessionStorage.setItem("loginEmail", emailValue);
		}
	}, [emailValue]);

	// Redirect if already authenticated
	useEffect(() => {
		console.log("Auth state changed - authenticated:", isAuthenticated, "initialized:", isInitialized);
		if (isAuthenticated && isInitialized) {
			console.log("User is authenticated, redirecting to dashboard");
			navigate(paths.dashboard.root);
		}
	}, [isAuthenticated, isInitialized, navigate]);

	// Handle form submission
	const onSubmit = async (data: LoginInput) => {
		console.log("FORM SUBMIT START");

		try {
			console.log("Calling login function with:", { email: data.email });
			// Call login function
			const result = await login(data);
			console.log("Login result received:", result);

			if (!result.success && result.error) {
				console.log("Login failed, setting error in URL:", result.error);
				// Set error in URL for display
				const errorUrl = `${location.pathname}?error=${encodeURIComponent(result.error)}`;
				navigate(errorUrl, { replace: true });
				// Clear password
				setValue("password", "", { shouldValidate: false });
			} else {
				console.log("Login successful, redirecting...");
				// Success is handled by the auth hook's redirect
			}
		} catch (error) {
			console.error("Unexpected error during login:", error);
			// Set error in URL for display
			const errorUrl = `${location.pathname}?error=${encodeURIComponent("An unexpected error occurred")}`;
			navigate(errorUrl, { replace: true });
			// Clear password
			setValue("password", "", { shouldValidate: false });
		}
	};

	console.log("Current render state - errorMessage:", errorMessage, "isLoggingIn:", isLoggingIn);

	// Show loading state while checking authentication
	if (!isInitialized) {
		console.log("Rendering loading state (not initialized)");
		return (
			<Center style={{ height: "100vh" }}>
				<Loader />
			</Center>
		);
	}

	console.log("Rendering login form");
	return (
		<Center h="100vh" w="100vw">
			<Container size={420} my={40}>
				<Title ta="center">Welcome back!</Title>

				<Paper withBorder shadow="md" p={30} mt={30} radius="md">
					<Box component="form" onSubmit={handleSubmit(onSubmit)}>
						<Stack>
							{errorMessage && (
								<Alert color="red" icon={<CircleAlert size={16} />}>
									{errorMessage}
								</Alert>
							)}

							<TextInput
								label="Email"
								placeholder="you@example.com"
								required
								{...register("email")}
								error={errors.email?.message}
							/>

							<PasswordInput
								label="Password"
								placeholder="Your password"
								required
								{...register("password")}
								error={errors.password?.message}
							/>

							<Button type="submit" loading={isLoggingIn}>
								Sign in
							</Button>
						</Stack>
					</Box>
				</Paper>
			</Container>
		</Center>
	);
}
